import {combine, merge, sample} from 'effector';

import {
    fetchProductDefectStatistics,
    fetchProductDynamicStatistics,
    fetchProductStatusStatistics,
    fetchProductTopStatistics,
    validateCode,
} from 'api/dashboard';
import {RestError, showRestError} from 'core/errors';
import {DashboardDateType} from 'types';
import {dashboardPeriod} from 'utils/dashboardPeriod';

import {
    activeStatusBarChart$,
    activeStatusBarChartChanged,
    activeTopProductCircle$,
    activeTopProductCircleChanged,
    codeValidation$,
    codeValidationFx,
    codeValidationParamsChanged,
    dateRange$,
    dateRangeChanged,
    dateType$,
    dateTypeChanged,
    Gate,
    loadProductDefectStatisticFx,
    loadProductDynamicStatisticsFx,
    loadProductStatusStatisticsFx,
    loadProductTopStatisticsFx,
    packageType$,
    packageTypeChanged,
    productDefect$,
    productDynamic$,
    productDynamicTotal$,
    productStatus$,
    productTop$,
    resetDates,
} from './index';

loadProductDynamicStatisticsFx.use((params) =>
    fetchProductDynamicStatistics(params.from, params.to, params.dateType, params.packageType),
);

loadProductStatusStatisticsFx.use((params) =>
    fetchProductStatusStatistics(params.from, params.to, params.dateType, params.packageType),
);

loadProductTopStatisticsFx.use((params) =>
    fetchProductTopStatistics(params.from, params.to, params.dateType, params.packageType),
);

loadProductDefectStatisticFx.use((params) =>
    fetchProductDefectStatistics(params.from, params.to, params.dateType, params.packageType),
);

codeValidationFx.use((params) => validateCode(params));

dateRange$
    .on(dateRangeChanged, (_, data) => data)
    .on(resetDates, () => dashboardPeriod(DashboardDateType.Month))
    .on(Gate.open, (range) =>
        dateType$.getState() === DashboardDateType.Period
            ? range
            : dashboardPeriod(dateType$.getState()),
    );

dateType$.on(dateTypeChanged, (_, data) => data).reset(resetDates);

packageType$.on(packageTypeChanged, (_, type) => type).reset(resetDates);

productDynamic$.on(loadProductDynamicStatisticsFx.doneData, (_, {data}) => data).reset(Gate.close);

productDynamicTotal$
    .on(loadProductDynamicStatisticsFx.doneData, (_, {total}) => total)
    .reset(Gate.close);

productStatus$.on(loadProductStatusStatisticsFx.doneData, (_, data) => data).reset(Gate.close);

productTop$.on(loadProductTopStatisticsFx.doneData, (_, data) => data).reset(Gate.close);

productDefect$.on(loadProductDefectStatisticFx.doneData, (_, data) => data).reset(Gate.close);
codeValidation$.on(codeValidationFx.doneData, (_, data) => data).reset(Gate.close);

activeStatusBarChart$.on(activeStatusBarChartChanged, (_, state) => state).reset(Gate.close);
activeTopProductCircle$.on(activeTopProductCircleChanged, (_, state) => state).reset(Gate.close);

sample({
    clock: merge([Gate.open, dateTypeChanged, resetDates, packageTypeChanged]),
    source: combine({
        dateRange: dateRange$,
        dateType: dateType$,
        packageType: packageType$,
    }),
    fn: (state) => ({
        from: state.dateRange.start,
        to: state.dateRange.end,
        dateType: state.dateType,
        packageType: state.packageType,
    }),
    target: [
        loadProductDynamicStatisticsFx,
        loadProductStatusStatisticsFx,
        loadProductDefectStatisticFx,
        loadProductTopStatisticsFx,
    ],
});

sample({
    clock: codeValidationParamsChanged,
    target: codeValidationFx,
});

loadProductDynamicStatisticsFx.failData.watch((err) => showRestError(err as RestError));
loadProductStatusStatisticsFx.failData.watch((err) => showRestError(err as RestError));
loadProductTopStatisticsFx.failData.watch((err) => showRestError(err as RestError));
loadProductDefectStatisticFx.failData.watch((err) => showRestError(err as RestError));
codeValidationFx.failData.watch((err) => showRestError(err as RestError));
